import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import apiClient from '../services/apiClient'
import Layout from '../components/Layout'
import NoticeBanner from '../components/NoticeBanner'
import Spinner from '../components/Spinner'
import ConfirmModal from '../components/ConfirmModal'

const QUESTION_LABELS = {
  time_efficiency:    'Time Efficiency',
  quality_output:     'Quality & Output',
  clarity_detail:     'Clarity & Detail',
  risk_prevention:    'Risk Prevention',
  value_collaboration:'Value & Collaboration',
  repeat_refine:      'Repeat & Refine',
}

function RatingDisplay({ value }) {
  if (!value) return <span style={{ color: 'var(--text-secondary)' }}>—</span>
  if (value === 'N/A') return <span style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem' }}>N/A</span>
  const n = parseInt(value, 10)
  return (
    <span title={`${value}/5`}>
      {'★'.repeat(n)}<span className="rating-star-empty">{'★'.repeat(5 - n)}</span>
    </span>
  )
}

export default function Results() {
  const { user, effectiveRole } = useAuth()
  const navigate   = useNavigate()
  const [searchParams] = useSearchParams()
  const isAdmin = effectiveRole === 'admin'

  const [surveys, setSurveys]         = useState([])
  const [total, setTotal]             = useState(0)
  const [page, setPage]               = useState(1)
  const [loading, setLoading]         = useState(false)
  const [expandedId, setExpandedId]   = useState(null)
  const [detail, setDetail]           = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)  // { id, submitted_by, experiment_name }

  // Filters
  const [experimentId, setExperimentId] = useState(searchParams.get('experiment_id') ?? '')
  const [teamId, setTeamId]             = useState('')
  const [briefTeamFilter, setBriefTeamFilter] = useState('')
  const [fromDate, setFromDate]         = useState('')
  const [toDate, setToDate]             = useState('')

  // Reference data for filter dropdowns
  const [experiments, setExperiments] = useState([])
  const [teams, setTeams]             = useState([])

  useEffect(() => {
    Promise.all([
      apiClient.get('/admin/experiments'),
      isAdmin ? apiClient.get('/admin/teams') : Promise.resolve({ data: { items: [] } }),
    ]).then(([expRes, tmRes]) => {
      setExperiments(expRes.data.items)
      setTeams(tmRes.data.items)
    })
  }, [isAdmin])

  const fetchSurveys = useCallback(() => {
    setLoading(true)
    const params = new URLSearchParams({ page, page_size: 20 })
    if (experimentId) params.set('experiment_id', experimentId)
    if (isAdmin && teamId) params.set('team_id', teamId)
    apiClient
      .get(`/surveys?${params}`)
      .then((res) => { setSurveys(res.data.items); setTotal(res.data.total) })
      .finally(() => setLoading(false))
  }, [page, experimentId, teamId, isAdmin])

  useEffect(() => { fetchSurveys() }, [fetchSurveys]) // eslint-disable-line react-hooks/set-state-in-effect

  // IDs of experiments belonging to the selected brief-team filter
  const briefTeamExpIds = useMemo(() => {
    if (!briefTeamFilter) return null
    return new Set(experiments.filter((e) => e.team_id === Number(briefTeamFilter)).map((e) => e.id))
  }, [experiments, briefTeamFilter])

  // Client-side date + brief-team filter on loaded page
  const filtered = surveys.filter((s) => {
    if (briefTeamExpIds && !briefTeamExpIds.has(s.experiment_id)) return false
    const dt = new Date(s.submitted_at)
    if (fromDate && dt < new Date(fromDate)) return false
    if (toDate   && dt > new Date(toDate + 'T23:59:59')) return false
    return true
  })

  const toggleRow = (id) => {
    if (expandedId === id) {
      setExpandedId(null)
      setDetail(null)
      return
    }
    setExpandedId(id)
    setDetail(null)
    setDetailLoading(true)
    apiClient
      .get(`/surveys/${id}`)
      .then((res) => setDetail(res.data))
      .finally(() => setDetailLoading(false))
  }

  const canEdit = (survey) => survey.submitted_by === user?.display_name

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await apiClient.delete(`/surveys/${deleteTarget.id}`)
      setDeleteTarget(null)
      if (expandedId === deleteTarget.id) {
        setExpandedId(null)
        setDetail(null)
      }
      fetchSurveys()
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Delete failed', err)
    }
  }

  return (
    <Layout>
      <div className="page-header">
        <h1 className="page-title">Survey Results</h1>
      </div>

      {/* Filter bar */}
      <div className="filter-bar">
        <div className="filter-group">
          <label htmlFor="res-brief-team-filter">AI Brief Team</label>
          <select
            id="res-brief-team-filter"
            value={briefTeamFilter}
            onChange={(e) => {
              setBriefTeamFilter(e.target.value)
              setExperimentId('')
              setPage(1)
            }}
          >
            <option value="">All</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="res-exp-filter">AI Brief</label>
          <select
            id="res-exp-filter"
            value={experimentId}
            onChange={(e) => { setExperimentId(e.target.value); setPage(1) }}
          >
            <option value="">All</option>
            {experiments
              .filter((e) => !briefTeamFilter || e.team_id === Number(briefTeamFilter))
              .map((e) => (
                <option key={e.id} value={e.id}>{e.experiment_name}</option>
              ))}
          </select>
        </div>

        {isAdmin && (
          <div className="filter-group">
            <label htmlFor="res-team-filter">Respondent Team</label>
            <select
              id="res-team-filter"
              value={teamId}
              onChange={(e) => { setTeamId(e.target.value); setPage(1) }}
            >
              <option value="">All teams</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="filter-group">
          <label htmlFor="res-from">From date</label>
          <input
            id="res-from"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label htmlFor="res-to">To date</label>
          <input
            id="res-to"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="loading-state">
          <Spinner size="md" />
          Loading results…
        </div>
      ) : (
        <>
          <p className="results-total">
            {total} survey{total !== 1 ? 's' : ''} found
          </p>

          <div className="card" style={{ padding: 0 }}>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>AI Brief</th>
                    <th>Submitted by</th>
                    <th>Team</th>
                    <th>Date</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>
                        No surveys found.
                      </td>
                    </tr>
                  )}

                  {filtered.map((s) => (
                    <>
                      <tr
                        key={s.id}
                        className="clickable"
                        onClick={() => toggleRow(s.id)}
                        aria-expanded={expandedId === s.id}
                      >
                        <td>{s.experiment_name}</td>
                        <td>{s.submitted_by}</td>
                        <td>{s.team_name ?? '—'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {new Date(s.submitted_at).toLocaleDateString()}
                        </td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {canEdit(s) && (
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              onClick={(e) => {
                                e.stopPropagation()
                                navigate(`/survey?experiment_id=${s.experiment_id}&edit=${s.id}`)
                              }}
                            >
                              Edit
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              style={{ marginLeft: '0.25rem' }}
                              onClick={(e) => {
                                e.stopPropagation()
                                setDeleteTarget({ id: s.id, submitted_by: s.submitted_by, experiment_name: s.experiment_name })
                              }}
                            >
                              Delete
                            </button>
                          )}
                          <span className="results-expand-indicator">
                            {expandedId === s.id ? '▲' : '▼'}
                          </span>
                        </td>
                      </tr>

                      {expandedId === s.id && (
                        <tr key={`detail-${s.id}`}>
                          <td colSpan={5} style={{ padding: 0 }}>
                            <div className="result-row-detail">
                              {detailLoading && (
                                <div className="loading-state" style={{ padding: '1rem' }}>
                                  <Spinner size="sm" />
                                  Loading…
                                </div>
                              )}

                              {detail && detail.id === s.id && (
                                <div style={{ padding: '0.5rem 0.25rem' }}>
                                  {detail.updated_at && (
                                    <NoticeBanner variant="info">
                                      Last edited: {new Date(detail.updated_at).toLocaleString()}
                                    </NoticeBanner>
                                  )}

                                  <div className="results-detail-meta">
                                    <div>
                                      <span className="results-meta-label">Roles: </span>
                                      {detail.selected_roles.join(', ') || '—'}
                                    </div>
                                    <div>
                                      <span className="results-meta-label">AI Tools: </span>
                                      {[...detail.selected_ai_tools, detail.other_ai_tools].filter(Boolean).join(', ') || '—'}
                                    </div>
                                    <div>
                                      <span className="results-meta-label">AI Models: </span>
                                      {[...detail.selected_ai_models, detail.other_ai_models].filter(Boolean).join(', ') || '—'}
                                    </div>
                                    <div>
                                      <span className="results-meta-label">Product Lifecycle: </span>
                                      {[...detail.selected_product_lifecycles, detail.other_product_lifecycle].filter(Boolean).join(', ') || '—'}
                                    </div>
                                  </div>

                                  <div className="table-container">
                                    <table className="data-table analytics-table">
                                      <thead>
                                        <tr>
                                          {Object.values(QUESTION_LABELS).map((l) => (
                                            <th key={l}>{l}</th>
                                          ))}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        <tr>
                                          {Object.keys(QUESTION_LABELS).map((k) => (
                                            <td key={k} className="analytics-cell-center">
                                              <span className="rating-star">
                                                <RatingDisplay value={detail.ratings?.[k]} />
                                              </span>
                                              {detail.comments?.[k] && (
                                                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                                                  {detail.comments[k]}
                                                </p>
                                              )}
                                            </td>
                                          ))}
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>

                                  {detail.general_comments && (
                                    <p style={{ fontSize: '0.875rem', marginTop: '0.75rem' }}>
                                      <strong>Comments:</strong> {detail.general_comments}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="results-pagination">
            <button className="btn btn-secondary btn-sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
              ← Prev
            </button>
            <span className="results-pagination-label">Page {page}</span>
            <button className="btn btn-secondary btn-sm" disabled={surveys.length < 20} onClick={() => setPage((p) => p + 1)}>
              Next →
            </button>
          </div>
        </>
      )}

      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Delete Survey Response"
        message={`Are you sure you want to permanently delete the survey response for AI Brief "${deleteTarget?.experiment_name}" submitted by "${deleteTarget?.submitted_by}"? This cannot be undone.`}
        confirmLabel="Delete"
        confirmVariant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Layout>
  )
}
